import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { MotionModal } from '../../../../components/motion-modal/motion-modal';
import { registerDevice, removeDevice } from '../../../../queries/ats/playback/devices/api';
import { useDevices } from '../../../../queries/ats/playback/devices/hooks';
import { Device } from '../../../../queries/ats/playback/devices/types';
import css from './device-selector.module.scss';
import { getDeletableDevices, getLocalizedRemainingTimeForNextSwap } from './utils';

interface DeviceSelectorProps {
  children?: React.ReactNode;
  onClose?: () => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function DeviceSelector({ children, onClose, onSuccess, onError }: DeviceSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const { data: devices = [] } = useDevices();

  const handleRegisterDevice = useCallback(async () => {
    try {
      await registerDevice();
      setIsOpen(false);
      onSuccess?.();
    } catch (err) {
      onError?.(err);
    }
  }, [onError, onSuccess]);

  const discardDevice = useCallback(async (deviceId: string) => await removeDevice(deviceId), []);

  const exit = useCallback(async () => {
    onClose?.();
    setIsOpen(false);
  }, [onClose]);

  const replacableDevices = getDeletableDevices(devices);

  if (!replacableDevices.length && devices.length) {
    return <DeviceSwapLimitReached exit={exit} isOpen devices={devices} />;
  }

  return (
    <MotionModal offsetTop={128} classNames={{ content: css.content }} isOpen={isOpen} onRequestClose={exit}>
      <header className={css.header}>
        <MotionModal.CloseButton onClick={exit} />
        <h2 className={css.headerText}>{t('change_device')}</h2>
        <p className={css.descText}>
          <Trans i18nKey={'change_device_message'} />
        </p>
      </header>

      <ul className={css.list}>
        {children}

        {replacableDevices.map((device) => (
          <li className={css.item} key={device.deviceId}>
            <MotionModal.Button
              onClick={() => {
                discardDevice(device.deviceId);
                handleRegisterDevice();
              }}
              className={css.button}
            >
              {device.deviceName}
            </MotionModal.Button>
          </li>
        ))}
      </ul>
    </MotionModal>
  );
}

interface PlayerErrorFallbackProps {
  exit: () => Promise<void>;
  isOpen: boolean;
  devices: Device[];
}

function DeviceSwapLimitReached({ exit, isOpen, devices }: PlayerErrorFallbackProps) {
  const history = useHistory();
  const timeRemainingMessage = getLocalizedRemainingTimeForNextSwap(devices);

  return (
    <MotionModal offsetTop={128} classNames={{ content: css.content }} isOpen={isOpen} onRequestClose={exit}>
      <header className={css.header}>
        <MotionModal.CloseButton onClick={() => history.goBack()} />
        <h2 className={css.headerText}>
          <Trans i18nKey={'cannot_swap_device'} />
        </h2>
        <p className={css.descText}>{timeRemainingMessage}</p>
      </header>
    </MotionModal>
  );
}
